import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { breachApi, organizationApi, openPdfExport } from '../services/api';
import {
  DataBreach,
  BreachStatus,
  BreachSeverity,
  BreachCategory,
  Organization
} from '@dsgvo-buddy/types';
import {
  Title,
  Button,
  Table,
  TableColumn,
  TableRow,
  TableCell,
  Input,
  Select,
  Option,
  FlexBox,
  Text,
  Icon,
  ObjectStatus,
  BusyIndicator,
  Card,
  CardHeader
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import PageIntro from '../components/PageIntro';
import { pageIntros } from '../helpers/helpTexts';

const statusConfig: Record<BreachStatus, { label: string; state: string }> = {
  [BreachStatus.DETECTED]: { label: 'Entdeckt', state: 'Warning' },
  [BreachStatus.TRIAGE]: { label: 'Triage', state: 'Warning' },
  [BreachStatus.INVESTIGATION]: { label: 'Untersuchung', state: 'Information' },
  [BreachStatus.AUTHORITY_NOTIFIED]: { label: 'Behörde benachrichtigt', state: 'Information' },
  [BreachStatus.SUBJECTS_NOTIFIED]: { label: 'Betroffene benachrichtigt', state: 'Information' },
  [BreachStatus.REMEDIATION]: { label: 'Gegenmaßnahmen', state: 'Information' },
  [BreachStatus.CLOSED]: { label: 'Abgeschlossen', state: 'Success' }
};

const severityConfig: Record<BreachSeverity, { label: string; state: string }> = {
  [BreachSeverity.LOW]: { label: 'Gering', state: 'Success' },
  [BreachSeverity.MEDIUM]: { label: 'Mittel', state: 'Warning' },
  [BreachSeverity.HIGH]: { label: 'Hoch', state: 'Error' },
  [BreachSeverity.CRITICAL]: { label: 'Kritisch', state: 'Error' }
};

const categoryConfig: Record<BreachCategory, { label: string }> = {
  [BreachCategory.UNAUTHORIZED_ACCESS]: { label: 'Unbefugter Zugriff' },
  [BreachCategory.DATA_LOSS]: { label: 'Datenverlust' },
  [BreachCategory.UNAUTHORIZED_DISCLOSURE]: { label: 'Unbefugte Weitergabe' },
  [BreachCategory.ACCIDENTAL_DELETION]: { label: 'Versehentliche Löschung' },
  [BreachCategory.DEVICE_LOSS]: { label: 'Geräteverlust' },
  [BreachCategory.RANSOMWARE]: { label: 'Ransomware' },
  [BreachCategory.MISCONFIGURATION]: { label: 'Fehlkonfiguration' },
  [BreachCategory.SOCIAL_ENGINEERING]: { label: 'Social Engineering' },
  [BreachCategory.OTHER]: { label: 'Sonstiges' }
};

function getDeadlineState(breach: DataBreach): { color: string; label: string } {
  const deadlineDate = new Date(breach.authority_deadline);
  const now = new Date();
  const diffMs = deadlineDate.getTime() - now.getTime();
  const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

  if (diffHours < 0) return { color: 'var(--sapNegativeColor)', label: `${Math.abs(diffHours)}h überfällig` };
  if (diffHours <= 12) return { color: 'var(--sapCriticalColor)', label: `Noch ${diffHours}h` };
  return { color: 'var(--sapPositiveColor)', label: `Noch ${diffHours}h` };
}

export default function BreachListPage() {
  const navigate = useNavigate();
  const { canCreate } = useUserRole();
  const [breaches, setBreaches] = useState<DataBreach[]>([]);
  const [filteredBreaches, setFilteredBreaches] = useState<DataBreach[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<BreachSeverity | ''>('');
  const [statusFilter, setStatusFilter] = useState<BreachStatus | ''>('');

  useEffect(() => { loadOrganizations(); }, []);
  useEffect(() => { if (currentOrg) { loadBreaches(); } }, [currentOrg]);
  useEffect(() => { applyFilters(); }, [breaches, searchTerm, severityFilter, statusFilter]);

  const loadOrganizations = async () => {
    const response = await organizationApi.getAll();
    if (response.success && response.data) {
      if (response.data.length > 0) setCurrentOrg(response.data[0]);
    }
    setLoading(false);
  };

  const loadBreaches = async () => {
    if (!currentOrg) return;
    setLoading(true);
    const response = await breachApi.getByOrganization(currentOrg.id);
    if (response.success && response.data) setBreaches(response.data);
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...breaches];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(b =>
        b.title.toLowerCase().includes(term) ||
        b.description?.toLowerCase().includes(term)
      );
    }
    if (severityFilter) filtered = filtered.filter(b => b.severity === severityFilter);
    if (statusFilter) filtered = filtered.filter(b => b.status === statusFilter);
    setFilteredBreaches(filtered);
  };

  if (loading && !currentOrg) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  return (
    <div className="page-layout">
      {/* Page Header */}
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>Datenschutzvorfälle</Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
            Melde- und Bearbeitungsprozess gemäß Art. 33/34 DSGVO
          </Text>
        </div>
        <FlexBox style={{ gap: '0.5rem' }}>
          <Button icon="pdf-attachment" design="Default" onClick={() => currentOrg && openPdfExport('breaches', currentOrg.id)}>PDF Export</Button>
          {canCreate && (
            <Button icon="add" design="Emphasized" onClick={() => navigate('/breaches/new')}>
              Neuen Vorfall melden
            </Button>
          )}
        </FlexBox>
      </FlexBox>

      <PageIntro {...pageIntros.breaches} storageKey="breaches" />

      {/* Filter Bar */}
      <FlexBox
        alignItems="End"
        wrap="Wrap"
        style={{
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '1rem',
          background: 'var(--sapObjectHeader_Background, var(--sapBaseColor))',
          borderRadius: '0.5rem',
          border: '1px solid var(--sapGroup_TitleBorderColor, #d9d9d9)'
        }}
      >
        <div style={{ flex: '1 1 300px', minWidth: '200px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Suche</Text>
          <Input
            placeholder="Titel, Beschreibung..."
            icon={<Icon name="search" />}
            value={searchTerm}
            onInput={(e: any) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ flex: '0 0 220px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Schweregrad</Text>
          <Select onChange={(e: any) => setSeverityFilter(e.detail.selectedOption.value)} style={{ width: '100%' }}>
            <Option value="">Alle Schweregrade</Option>
            {Object.entries(severityConfig).map(([key, cfg]) => (
              <Option key={key} value={key}>{cfg.label}</Option>
            ))}
          </Select>
        </div>

        <div style={{ flex: '0 0 180px' }}>
          <Text style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.25rem' }}>Status</Text>
          <Select onChange={(e: any) => setStatusFilter(e.detail.selectedOption.value)} style={{ width: '100%' }}>
            <Option value="">Alle Status</Option>
            {Object.entries(statusConfig).map(([key, cfg]) => (
              <Option key={key} value={key}>{cfg.label}</Option>
            ))}
          </Select>
        </div>

        {(searchTerm || severityFilter || statusFilter) && (
          <Button
            icon="decline"
            design="Transparent"
            onClick={() => { setSearchTerm(''); setSeverityFilter(''); setStatusFilter(''); }}
            tooltip="Filter zurücksetzen"
          />
        )}
      </FlexBox>

      {/* Table Card */}
      <Card style={{ overflow: 'hidden' }}
        header={
          <CardHeader
            titleText={`${filteredBreaches.length} von ${breaches.length} Vorfällen`}
            subtitleText="Klicken Sie auf eine Zeile für Details"
            avatar={<Icon name="alert" />}
          />
        }
      >
        {loading ? (
          <FlexBox alignItems="Center" justifyContent="Center" style={{ padding: '3rem' }}>
            <BusyIndicator active size="Large" />
          </FlexBox>
        ) : filteredBreaches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Icon name="alert" style={{ fontSize: '3rem', color: 'var(--sapNeutralColor)', display: 'block', marginBottom: '1rem' }} />
            <Title level="H5" style={{ marginBottom: '0.5rem' }}>
              {breaches.length === 0 ? 'Noch keine Vorfälle' : 'Keine Treffer'}
            </Title>
            <Text style={{ display: 'block', color: 'var(--sapNeutralTextColor)', marginBottom: '1rem' }}>
              {breaches.length === 0
                ? 'Erfassen Sie Ihren ersten Datenschutzvorfall.'
                : 'Passen Sie Ihre Filterkriterien an.'}
            </Text>
            {breaches.length === 0 && canCreate && (
              <Button icon="add" design="Emphasized" onClick={() => navigate('/breaches/new')}>
                Neuen Vorfall melden
              </Button>
            )}
          </div>
        ) : (
          <div style={{ padding: '0 1rem 1rem' }}>
            <Table
              style={{ width: '100%' }}
              onRowClick={(e: any) => {
                const index = parseInt(e.detail.row.dataset.index);
                navigate(`/breaches/${filteredBreaches[index].id}`);
              }}
              columns={
                <>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Vorfall</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Entdeckt</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>72h-Frist</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Schweregrad</Text></TableColumn>
                  <TableColumn><Text style={{ fontWeight: '600' }}>Status</Text></TableColumn>
                </>
              }
            >
              {filteredBreaches.map((breach, index) => {
                const deadlineInfo = getDeadlineState(breach);
                const isTerminal = breach.status === BreachStatus.CLOSED;
                return (
                  <TableRow key={breach.id} data-index={index} type="Active">
                    <TableCell>
                      <div>
                        <Text style={{ fontWeight: '600' }}>{breach.title}</Text>
                        <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginTop: '0.125rem' }}>
                          {categoryConfig[breach.category]?.label ?? breach.category}
                        </Text>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Text>{new Date(breach.detected_at).toLocaleDateString('de-DE')}</Text>
                    </TableCell>
                    <TableCell>
                      <Text style={{ color: isTerminal ? 'var(--sapNeutralTextColor)' : deadlineInfo.color, fontWeight: isTerminal ? 'normal' : '600' }}>
                        {isTerminal
                          ? new Date(breach.authority_deadline).toLocaleDateString('de-DE')
                          : deadlineInfo.label}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <ObjectStatus state={severityConfig[breach.severity]?.state as any} showDefaultIcon>
                        {severityConfig[breach.severity]?.label ?? breach.severity}
                      </ObjectStatus>
                    </TableCell>
                    <TableCell>
                      <ObjectStatus state={statusConfig[breach.status]?.state as any} showDefaultIcon>
                        {statusConfig[breach.status]?.label ?? breach.status}
                      </ObjectStatus>
                    </TableCell>
                  </TableRow>
                );
              })}
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
